// Record sharing panel component
// Allows sharing records with other users
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ShareIcon, TrashIcon, UserPlusIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { apiRequest } from '../../lib/api'
import { useAssignableUsers } from '../../hooks/useUsers'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'
import { Avatar } from '../ui/Avatar'

interface RecordShare {
  id: string
  entity: string
  entityId: string
  accessLevel: 'read' | 'read_write'
  reason?: string
  expiresAt?: string
  createdAt: string
  sharedWithUser: {
    id: string
    email: string
    firstName?: string
    lastName?: string
  }
}

interface SharesResponse {
  data: RecordShare[]
}

interface Props {
  entity: 'leads' | 'accounts' | 'contacts' | 'opportunities' | 'events' | 'notes'
  entityId: string
  ownerId?: string
}

export function RecordSharePanel({ entity, entityId, ownerId }: Props) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [showAddForm, setShowAddForm] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState('')
  const [accessLevel, setAccessLevel] = useState<'read' | 'read_write'>('read')
  const [reason, setReason] = useState('')
  const [expiresAt, setExpiresAt] = useState('')

  // Fetch existing shares
  const { data: sharesData, isLoading } = useQuery({
    queryKey: ['record-shares', entity, entityId],
    queryFn: () => apiRequest<SharesResponse>(`/record-shares/${entity}/${entityId}`),
  })

  // Fetch assignable users
  const { data: usersData } = useAssignableUsers()

  const shares = sharesData?.data || []
  const users = usersData?.data || []

  // Filter out users who already have access
  const sharedUserIds = new Set(shares.map(s => s.sharedWithUser.id))
  const availableUsers = users.filter(u =>
    u.id !== user?.id &&
    u.id !== ownerId &&
    !sharedUserIds.has(u.id)
  )

  // Create share mutation
  const createShare = useMutation({
    mutationFn: (data: { sharedWithUserId: string; accessLevel: string; reason?: string; expiresAt?: string }) =>
      apiRequest('/record-shares', {
        method: 'POST',
        body: JSON.stringify({
          entity,
          entityId,
          ...data,
        }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['record-shares', entity, entityId] })
      resetForm()
    },
  })

  // Delete share mutation
  const deleteShare = useMutation({
    mutationFn: (shareId: string) =>
      apiRequest(`/record-shares/${shareId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['record-shares', entity, entityId] })
    },
  })

  const resetForm = () => {
    setShowAddForm(false)
    setSelectedUserId('')
    setAccessLevel('read')
    setReason('')
    setExpiresAt('')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedUserId) return

    createShare.mutate({
      sharedWithUserId: selectedUserId,
      accessLevel,
      reason: reason || undefined,
      expiresAt: expiresAt || undefined,
    })
  }

  // Check if current user can share (owner or admin)
  const canShare = user?.role === 'admin' || user?.id === ownerId

  if (!canShare) {
    return null
  }

  return (
    <div className="border-t border-gray-200 pt-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <ShareIcon className="h-4 w-4" />
          {t('sharing.sharedWith')}
        </h3>
        {!showAddForm && availableUsers.length > 0 && (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700"
          >
            <UserPlusIcon className="h-4 w-4" />
            {t('sharing.addShare')}
          </button>
        )}
      </div>

      {/* Add share form */}
      {showAddForm && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('sharing.shareWith')}
              </label>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
                required
              >
                <option value="">{t('sharing.selectUser')}</option>
                {availableUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.firstName && u.lastName
                      ? `${u.firstName} ${u.lastName}`
                      : u.email}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('sharing.accessLevel')}
              </label>
              <select
                value={accessLevel}
                onChange={(e) => setAccessLevel(e.target.value as 'read' | 'read_write')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
              >
                <option value="read">{t('sharing.readOnly')}</option>
                <option value="read_write">{t('sharing.readWrite')}</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('sharing.reason')} ({t('common.optional')})
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('sharing.reasonPlaceholder')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('sharing.expiresAt')} ({t('common.optional')})
              </label>
              <input
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={resetForm}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={createShare.isPending || !selectedUserId}
                className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {createShare.isPending ? t('common.saving') : t('sharing.share')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Shares list */}
      {isLoading ? (
        <div className="text-sm text-gray-500">{t('common.loading')}</div>
      ) : shares.length === 0 ? (
        <p className="text-xs text-gray-500">{t('sharing.noShares')}</p>
      ) : (
        <div className="space-y-2">
          {shares.map((share) => (
            <div
              key={share.id}
              className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center gap-2">
                <Avatar
                  firstName={share.sharedWithUser.firstName || '?'}
                  lastName={share.sharedWithUser.lastName}
                  size="xs"
                />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {share.sharedWithUser.firstName && share.sharedWithUser.lastName
                      ? `${share.sharedWithUser.firstName} ${share.sharedWithUser.lastName}`
                      : share.sharedWithUser.email}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${
                      share.accessLevel === 'read_write'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {share.accessLevel === 'read_write' ? t('sharing.readWrite') : t('sharing.readOnly')}
                    </span>
                    {share.reason && <span>{share.reason}</span>}
                    {share.expiresAt && (
                      <span className="text-orange-600">
                        {t('sharing.expiresOn')} {new Date(share.expiresAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (confirm(t('sharing.confirmRemove'))) {
                    deleteShare.mutate(share.id)
                  }
                }}
                disabled={deleteShare.isPending}
                className="text-red-500 hover:text-red-700 p-1"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
